/**
 * Copyright (C) 2016 Maxime Petazzoni <maxime.petazzoni@bulix.org>.
 * All rights reserved.
 *
 * Moved here since sse.js does not support esm modules it appears
 */

const FIELD_SEPARATOR = ':'

type Listener = (event: Event) => void

export class SSE {
  static INITIALIZING = -1
  static CONNECTING = 0
  static OPEN = 1
  static CLOSED = 2

  private url: string
  private headers: Record<string, string>
  private payload: string
  private method: 'PORT' | 'GET'
  private withCredentials: boolean
  private readyState: number
  private listeners: Record<string, ((event: Event) => void)[]>
  private progress: number
  private chunk: string
  private xhr: XMLHttpRequest | null

  constructor(url: string, options: Record<string, any>) {
    this.url = url

    options = options || {}
    this.headers = options.headers || {}
    this.payload = options.payload !== undefined ? options.payload : ''
    this.method = options.method || (this.payload && 'POST') || 'GET'
    this.withCredentials = !!options.withCredentials

    this.listeners = {}

    this.xhr = null
    this.readyState = SSE.INITIALIZING
    this.progress = 0
    this.chunk = ''
  }

  addEventListener(type: string, listener: Listener) {
    if (this.listeners[type] === undefined) {
      this.listeners[type] = []
    }

    if (this.listeners[type].indexOf(listener) === -1) {
      this.listeners[type].push(listener)
    }
  }

  removeEventListener(type: string, listener: Listener) {
    if (this.listeners[type] === undefined) {
      return
    }

    const filtered: Listener[] = []
    this.listeners[type].forEach(function (element) {
      if (element !== listener) {
        filtered.push(element)
      }
    })
    if (filtered.length === 0) {
      delete this.listeners[type]
    } else {
      this.listeners[type] = filtered
    }
  }

  dispatchEvent(event: CustomEvent) {
    if (!event) {
      return true
    }

    // @ts-ignore
    event.source = this

    var onHandler = 'on' + event.type
    if (this.hasOwnProperty(onHandler)) {
      // @ts-ignore
      this[onHandler].call(this, event)
      if (event.defaultPrevented) {
        return false
      }
    }

    if (this.listeners[event.type]) {
      return this.listeners[event.type].every(function (callback) {
        callback(event)
        return !event.defaultPrevented
      })
    }

    return true
  }

  _setReadyState(state: number) {
    const event = new CustomEvent('readystatechange')
    // @ts-ignore
    event.readyState = state
    this.readyState = state
    this.dispatchEvent(event)
  }

  _onStreamFailure(e: any) {
    this.dispatchEvent(new CustomEvent('error'))
    this.close()
  }

  _onStreamProgress(e: any) {
    if (!this.xhr) {
      return
    }

    if (this.xhr.status !== 200) {
      this._onStreamFailure(e)
      return
    }

    if (this.readyState == SSE.CONNECTING) {
      this.dispatchEvent(new CustomEvent('open'))
      this._setReadyState(SSE.OPEN)
    }

    var data = this.xhr.responseText.substring(this.progress)
    this.progress += data.length
    data.split(/(\r\n|\r|\n){2}/g).forEach((part) => {
      if (part.trim().length === 0) {
        const chunkEvent = this._parseEventChunk(this.chunk.trim())
        if (chunkEvent) {
          this.dispatchEvent(chunkEvent)
        }
        this.chunk = ''
      } else {
        this.chunk += part
      }
    })
  }

  _onStreamLoaded(event: Event) {
    this._onStreamProgress(event)
    // Parse the last chunk.
    const chunkEvent = this._parseEventChunk(this.chunk)
    if (chunkEvent) {
      this.dispatchEvent(chunkEvent)
    }
    this.chunk = ''
  }

  /**
   * Parse a received SSE event chunk into a constructed event object.
   */
  _parseEventChunk(chunk: string): CustomEvent | null {
    if (!chunk || chunk.length === 0) {
      return null
    }

    const e: Record<string, any> = {
      id: null,
      retry: null,
      data: '',
      event: 'message'
    }
    chunk.split(/\n|\r\n|\r/).forEach((line: string) => {
      line = line.trimEnd()
      var index = line.indexOf(FIELD_SEPARATOR)
      if (index <= 0) {
        // Line was either empty, or started with a separator and is a comment.
        // Either way, ignore.
        return
      }

      const field = line.substring(0, index)
      if (!(field in e)) {
        return
      }

      const value = line.substring(index + 1).trimStart()
      if (field === 'data') {
        e[field] += value
      } else {
        e[field] = value
      }
    })

    const event = new CustomEvent(e.event)
    // @ts-ignore
    event.data = e.data
    // @ts-ignore
    event.id = e.id
    return event
  }

  _checkStreamClosed() {
    if (!this.xhr) {
      return
    }

    if (this.xhr.readyState === XMLHttpRequest.DONE) {
      this._setReadyState(SSE.CLOSED)
    }
  }

  stream() {
    this._setReadyState(SSE.CONNECTING)

    this.xhr = new XMLHttpRequest()
    this.xhr.addEventListener('progress', this._onStreamProgress.bind(this))
    this.xhr.addEventListener('load', this._onStreamLoaded.bind(this))
    this.xhr.addEventListener('readystatechange', this._checkStreamClosed.bind(this))
    this.xhr.addEventListener('error', this._onStreamFailure.bind(this))
    this.xhr.addEventListener('abort', this._onStreamFailure.bind(this))
    this.xhr.open(this.method, this.url)
    for (var header in this.headers) {
      this.xhr.setRequestHeader(header, this.headers[header])
    }
    this.xhr.withCredentials = this.withCredentials
    this.xhr.send(this.payload)
  }

  close() {
    if (this.readyState === SSE.CLOSED) {
      return
    }

    if (this.xhr) {
      this.xhr.abort()
      this.xhr = null
    }
    this._setReadyState(SSE.CLOSED)
  }
}
